import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Package, CheckCircle, Clock, XCircle, ArrowRight } from 'lucide-react';
import { getTicketByCodeAndEmail } from '../../services/ticketService';
import toast from 'react-hot-toast';

const TrackTicket = () => {
  const [ticketCode, setTicketCode] = useState('');
  const [email, setEmail] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!ticketCode.trim() || !email.trim()) {
      toast.error('Please enter both ticket number and email');
      return;
    }

    setIsSearching(true);
    setNotFound(false);
    setTicket(null);
    setDebugInfo(null);

    try {
      console.log('Searching for ticket:', { ticketCode: ticketCode.trim(), email: email.trim() });
      const result = await getTicketByCodeAndEmail(ticketCode.trim(), email.trim());
      console.log('Ticket search result:', result);

      if (result) {
        setTicket(result);
        toast.success('Ticket found!');
        setDebugInfo({ success: true, message: 'Ticket found successfully' });
      } else {
        setNotFound(true);
        console.log('Ticket not found - showing not found message');
        setDebugInfo({ success: false, message: 'No matching ticket found in database' });
      }
    } catch (error) {
      console.error('Error tracking ticket:', error);
      console.error('Error details:', {
        code: error?.code,
        message: error?.message,
        name: error?.name
      });
      // Treat errors as "not found" to avoid confusing users
      setNotFound(true);
      setDebugInfo({
        success: false,
        error: error?.code || error?.message || 'Unknown error',
        message: 'Check browser console (F12) for details'
      });
    } finally {
      setIsSearching(false);
    }
  };

  const getStatusStep = (status) => {
    const statusMap = {
      'open': 0,
      'in_progress': 1,
      'resolved': 2,
      'closed': 3,
    };
    return statusMap[status] || 0;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Pending';
    if (timestamp.toDate) return timestamp.toDate().toLocaleString();
    const asDate = new Date(timestamp);
    if (Number.isNaN(asDate.getTime())) return 'Pending';
    return asDate.toLocaleString();
  };

  const currentStep = ticket ? getStatusStep(ticket.status) : -1;

  const steps = [
    { label: 'Submitted', icon: Package, status: 'open' },
    { label: 'In Progress', icon: Clock, status: 'in_progress' },
    { label: 'Resolved', icon: CheckCircle, status: 'resolved' },
    { label: 'Closed', icon: CheckCircle, status: 'closed' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center -space-x-[5px]">
            <Link to="/" className="flex items-center -space-x-[5px]">
              <img
                src="/VetriaAI.png"
                alt="VetriaAI Logo"
                className="w-10 h-10 rounded-2xl"
              />
              <span className="text-2xl font-bold text-gray-900">VetriaAI</span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/login"
              className="bubbly-button bg-red-800 text-white hover:bg-red-900"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Search className="w-10 h-10 text-red-800" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Track Your Support Ticket</h1>
          <p className="text-lg text-gray-600">
            Enter your ticket number and email to check the status of your support request
          </p>
        </div>

        {/* Search Form */}
        <div className="bubbly-card p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Ticket Number <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={ticketCode}
                onChange={(e) => setTicketCode(e.target.value)}
                placeholder="e.g., TKT-ABCD123-456"
                className="bubbly-input w-full"
                required
              />
              <p className="text-xs text-gray-500 mt-1.5">
                You received this in your confirmation email
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter the email used to create the ticket"
                className="bubbly-input w-full"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSearching}
              className="bubbly-button w-full bg-red-800 text-white hover:bg-red-900 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Track Ticket</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Ticket Not Found */}
        {notFound && (
          <div className="bubbly-card p-8 text-center">
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Ticket Not Found</h3>
            <p className="text-gray-600 mb-6">
              We couldn't find a ticket with the provided details. Please check your ticket number and email address.
            </p>

            {/* Debug Info */}
            {debugInfo && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-xs font-mono text-gray-700 mb-2">
                  <strong>Debug Info:</strong>
                </p>
                <p className="text-xs font-mono text-gray-600">
                  {debugInfo.message}
                </p>
                {debugInfo.error && (
                  <p className="text-xs font-mono text-red-600 mt-1">
                    Error: {debugInfo.error}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  💡 Possible issues: Firestore rules not deployed, ticket doesn't exist, or email/ticket code mismatch
                </p>
              </div>
            )}

            <Link
              to="/help-center"
              className="bubbly-button inline-flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              Visit Help Center
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Ticket Status Display */}
        {ticket && (
          <div className="space-y-6">
            {/* Ticket Info Card */}
            <div className="bubbly-card p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {ticket.ticketCode || ticket.id}
                  </h2>
                  <p className="text-gray-600">{ticket.subject}</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${ticket.status === 'closed' ? 'bg-gray-100 text-gray-700' :
                  ticket.status === 'resolved' ? 'bg-green-100 text-green-700' :
                    ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-yellow-100 text-yellow-700'
                  }`}>
                  {ticket.status === 'in_progress' ? 'In Progress' : ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                </span>
              </div>

              {/* Progress Tracker */}
              <div className="relative mb-8">
                {/* Progress Line */}
                <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200 mx-12">
                  <div
                    className="h-full bg-blue-600 transition-all duration-500"
                    style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                  />
                </div>

                {/* Steps */}
                <div className="relative grid grid-cols-4 gap-4">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = index <= currentStep;
                    const isCurrent = index === currentStep;

                    return (
                      <div key={step.status} className="flex flex-col items-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${isCompleted
                          ? 'bg-blue-600 text-white shadow-lg'
                          : 'bg-gray-200 text-gray-400'
                          } ${isCurrent ? 'ring-4 ring-blue-200' : ''}`}>
                          <StepIcon className="w-8 h-8" />
                        </div>
                        <p className={`text-sm font-medium text-center ${isCompleted ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                          {step.label}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Ticket Details */}
              <div className="border-t border-gray-200 pt-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-medium text-gray-900 capitalize">{ticket.type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Priority</p>
                    <p className="font-medium text-gray-900 capitalize">{ticket.priority}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="font-medium text-gray-900">{formatDate(ticket.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="font-medium text-gray-900">{formatDate(ticket.updatedAt)}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-2">Description</p>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{ticket.description}</p>
                </div>
              </div>
            </div>

            {/* Expected Response Time */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 flex items-start gap-4">
              <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Expected Response Time</h3>
                <p className="text-sm text-blue-800">
                  {ticket.priority === 'urgent' && 'Urgent priority: 4-12 hours'}
                  {ticket.priority === 'high' && 'High priority: 12-24 hours'}
                  {(ticket.priority === 'medium' || ticket.priority === 'low') && 'Low/Medium priority: 24-48 hours'}
                </p>
              </div>
            </div>

            {/* Help Links */}
            <div className="text-center">
              <p className="text-gray-600 mb-4">Need more help?</p>
              <div className="flex justify-center gap-4">
                <Link
                  to="/help-center"
                  className="bubbly-button bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  Visit Help Center
                </Link>
                <Link
                  to="/contact"
                  className="bubbly-button bg-red-800 text-white hover:bg-red-900"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6 mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center -space-x-[5px] mb-4">
                <img
                  src="/VetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">VetriaAI</span>
              </div>
              <p className="text-gray-400">
                Revolutionizing recruitment with AI-powered intelligence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-800">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/features" className="text-white hover:text-gray-300">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-white hover:text-gray-300">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-800">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-white hover:text-gray-300">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white hover:text-gray-300">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-800">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/help-center" className="text-white hover:text-gray-300">
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link to="/track-ticket" className="text-white hover:text-gray-300">
                    Track Ticket
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-800">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/cancellations-refunds" className="text-white hover:text-gray-300">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-white hover:text-gray-300">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-white hover:text-gray-300">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-white hover:text-gray-300">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 VetriaAI. All rights and trademarks are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TrackTicket;
